import { memo, useState } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { Loading03Icon, StopIcon } from "@hugeicons/core-free-icons";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import type { FetchAllProgress } from "../types/orderAnalytics.types";

interface FetchAllProgressDialogProps {
  progress: FetchAllProgress | null;
  onAbort: () => void;
}

/**
 * 모두 가져오기 진행 팝업. 진행 중에는 화면을 막아 기간 변경·카드 조작을 못 하게 하고,
 * 빠져나갈 길은 중단 버튼 하나만 둔다.
 */
export const FetchAllProgressDialog = memo(function FetchAllProgressDialog({
  progress,
  onAbort,
}: FetchAllProgressDialogProps) {
  // 닫히는 애니메이션 동안 숫자가 0/0 으로 깜빡이지 않게 마지막 진행 상황을 붙잡아 둔다.
  const [shown, setShown] = useState(progress);
  if (progress && progress !== shown) setShown(progress);

  const total = shown?.total ?? 0;
  const done = shown?.done ?? 0;
  const failed = shown?.failed ?? 0;
  const percent = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <AlertDialog open={progress !== null}>
      <AlertDialogContent size="sm" className="gap-4">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-sm">
            <HugeiconsIcon
              icon={Loading03Icon}
              size={16}
              className="animate-spin text-blue-600"
            />
            모두 가져오는 중
          </AlertDialogTitle>
          <AlertDialogDescription className="text-xs">
            DB 부하를 줄이기 위해 한 번에 한 업체씩 순서대로 조회합니다.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2">
          <div className="flex items-baseline justify-between">
            <span className="text-2xl font-bold text-gray-900 tabular-nums">
              {done}
              <span className="text-sm font-medium text-gray-400"> / {total}</span>
            </span>
            {failed > 0 && (
              <span className="text-xs text-red-600">실패 {failed}건</span>
            )}
          </div>
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={done}
            className="h-1.5 overflow-hidden rounded-full bg-gray-100"
          >
            <div
              className="h-full rounded-full bg-blue-600 transition-[width]"
              style={{ width: `${percent}%` }}
            />
          </div>
          <p className="truncate text-xs text-gray-500">
            {shown?.currentName ? `조회 중: ${shown.currentName}` : "준비 중..."}
          </p>
        </div>

        <AlertDialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onAbort}
            className="text-xs"
          >
            <HugeiconsIcon icon={StopIcon} size={14} className="mr-1" />
            중단
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
});
